import React, { useEffect, useState,useRef } from 'react'
import {Tooltip,Typography,Popover,Slider,Grid,TextField, InputAdornment} from '@mui/material';
import FlareIcon from '@mui/icons-material/Flare';
import { useProjectData} from '../MattoState'
import {BarIconButton} from './ButtonStyles'
import { invalidate } from '@react-three/fiber';
import { setProfileFirstAction } from '../libs/util';
import { ProfileFirstActions } from '../../../../packages/ts-interfaces/index.ts';

export function LightingButton(props) {
	const [anchorEl, setAnchorEl] = useState(null)
	const [open, setOpen] = useState(false);
	const projectData = useProjectData()
	const setProjectData = useProjectData(state=>state.set)
	const [directionalValue, setDirectionalValue] = useState(0)

	useEffect(() => {
		setDirectionalValue(projectData.lighting.directional)
	}, [projectData.lighting.directional])

	const handleDirectionalChange = (event,newValue) => {	
		const hack = window.scene?.children?.filter(c => c.name=="directional light")?.[0]
		if (hack?.position?.x) {
			hack.position.x = newValue;
			invalidate()
		}
		else { 
			setProjectData(state => { state.lighting.directional = newValue })
		}
		setDirectionalValue(newValue)
	}
	const handleDirectionalCommit = (event,newValue) => {
		setProjectData(state => { state.lighting.directional = newValue })
	}
	const handleDirectionalLightChange = (event,newValue)=> {
		setProjectData(state => { state.lighting.directionalIntensity  = newValue })
	}
	const handleColorTempChange = (event,newValue) => {
		setProjectData(state=> {state.lighting.colorTemperature = newValue})
	}
	
	const handleClick = () => (event) => {
		setProfileFirstAction(ProfileFirstActions.OPEN_LIGHTING).finally(() => {})
		setAnchorEl(event.currentTarget);
		setOpen(!open)
	};
	const handleClose = () => {  setOpen(false) };
	return (
        <React.Fragment>
		<Tooltip title="Lighting" arrow>
		<BarIconButton
			sx={{paddingTop: { xs:'0px',sm: '0px', md: '12px' }, paddingBottom: { xs:'0px',sm: '0px', md: '12px' }}}
            onClick={handleClick()}
            aria-label="download"
            edge='start'
            size="large">
		<FlareIcon style={{color:'black'}}  />
		</BarIconButton>
		</Tooltip>
		<Popover
		  open={open}
		  anchorEl={anchorEl}
		  onClose={handleClose}
		  anchorOrigin={{ vertical: 'bottom',horizontal: 'center',}}
		  transformOrigin={{vertical: 'top',horizontal: 'left',}}
		>
		  <Grid container alignItems="center" style={{overflow:'hidden',minWidth:'265px', maxWidth: '500px', background:'#ebebeb', padding:'4px 12px', borderRadius:'6px'}}>
			<LightingSlider name="Color Temperature" max={17500} min={2000} step={50} value={projectData.lighting.colorTemperature} onChange={handleColorTempChange} />
			<LightingSlider name="Light Intensity" max={12} min={0} step={0.01} value={projectData.lighting.directionalIntensity} onChange={handleDirectionalLightChange} />
			<LightingSlider name="Light Position" max={95} min={-95} step={0.01} value={directionalValue} onChangeCommitted={handleDirectionalCommit} onChange={handleDirectionalChange} />
		  </Grid>
		</Popover>
	  </React.Fragment>
    );
}
const typographyStyle = {color:'#737373',fontSize:'14px'}
const gridStyle={paddingLeft:'10px', paddingTop:'1px', display:'flex'}

function LightingSlider({name, max, min, step, value, onChange, onChangeCommitted}) {
	// Add unit suffix based on the slider name
	const getUnit = () => {
	  if (name === "Color Temperature") return "K";
	  if (name === "Light Intensity") return "lm";
	  if (name === "Light Position") return "°";
	  return "";
	};

	// Convert internal value to display value
	const getDisplayValue = () => {
		if (name === "Light Position") {
			// Convert from -95 to 95 range to 0 to 180 range
			return Math.round(((value - min) / (max - min)) * 180);
		} else if (name === "Light Intensity") {
			// Convert internal intensity to lumens. lumens = intensity * 683. This is because 683 lumens is the equivalent of 1 watt of light. 
			return Math.round(value * 683);
		}
		return Math.round(value);
	};
  
	// Convert display value back to internal value
	const getInternalValue = (displayValue) => {
		if (name === "Light Position") {
			// Convert from 0 to 180 range back to -95 to 95 range
			return min + (displayValue / 180) * (max - min);
		} else if (name === "Light Intensity") {
			// Convert lumens back to internal value
			return displayValue / 683; // 3000 lumens / 250 = 12 internal max
		}
		return displayValue;
	};

	// Handle direct text input changes
	const handleTextChange = (event) => {
		let displayValue = parseFloat(event.target.value);
		if (!isNaN(displayValue) && displayValue >= 0) {
			if (name === "Light Position") {
				displayValue = Math.max(0, Math.min(180, displayValue));
				const internalValue = getInternalValue(displayValue);
				onChange(null, internalValue);
				if (onChangeCommitted) {
					onChangeCommitted(null, internalValue);
				}
			} else if (name === "Light Intensity") {
				displayValue = Math.max(0, Math.min(max*683, displayValue));
				const internalValue = getInternalValue(displayValue);
				onChange(null, internalValue);
				if (onChangeCommitted) {
					onChangeCommitted(null, internalValue);
				}
			} else {
				// For other sliders, check against min/max as before
				displayValue = Math.max(0, Math.min(max, displayValue));
				onChange(null, displayValue);
				if (onChangeCommitted) {
					onChangeCommitted(null, displayValue);
				}
			}
		}
	};
  
	return (
	  <React.Fragment>
			<Grid item xs={4}>
			<Typography style={typographyStyle}>{name}:</Typography>
			</Grid>
			<Grid item xs={5.5} style={gridStyle}>
			<Slider 
				size="small" 
				style={{color:'#000'}} 
				max={max} 
				min={min} 
				step={step} 
				value={value} 
				onChangeCommitted={onChangeCommitted} 
				onChange={onChange} 
				aria-labelledby="continuous-slider" 
			/>
			</Grid>
			<Grid item xs={2.5} style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '8px'}}>
				<TextField
					size="small"
					value={getDisplayValue()}
					onChange={handleTextChange}
					InputProps={{
					sx: {
						input: { textAlign: 'center' },
					},
					style: {
						fontSize: '12px',
						width: '64px',
						height: '20px',
						textAlign: 'center'
					},
					}}
					variant="outlined"
				/>
				<Typography style={{...typographyStyle, fontSize:'12px', paddingLeft:'4px'}}>{getUnit()}</Typography>
			</Grid>
	  </React.Fragment>
	);
  }